import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Container, Step, StepLabel, Stepper, Typography } from '@mui/material';
import type { Brand } from '@traaker/shared';
import { isOnboardingComplete } from '@traaker/shared';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../services/api';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { BrandFavicon } from '../components/brand/BrandFavicon';
import { ONBOARDING_BG } from './onboarding/onboardingShared';
import { BrandSetupStep } from './onboarding/BrandSetupStep';
import { MarketStep } from './onboarding/MarketStep';
import { PromptsStep } from './onboarding/PromptsStep';
import { runPendingTargets, type PendingTarget } from './onboarding/pendingTargets';
import type { GscCallbackResult } from '@traaker/shared';
import { isGscCallbackResult } from '../components/prompt-studio/searchConsoleModel';
import { setPendingGscReturn } from '../components/prompt-studio/searchConsoleStore';

// ─────────────────────────────────────────────────────────────────────────────
// Brand onboarding wizard (3 pages, 2026-11-03 rewrite — docs/features/onboarding.md).
//
//   1 « Votre marque »  — /onboarding/brand/new: analyze the site, create the brand,
//                          save + validate the Brand Strategy (BrandSetupStep)
//   2 « Votre marché »  — themes, use cases, criteria, competitors; the GEO Strategy is
//                          generated + validated silently (MarketStep)
//   3 « Vos prompts »   — the Prompt Studio composer + a starter batch; « Lancer » runs the
//                          prompts only (PromptsStep)
//
// Each page ends by VALIDATING what the next one's backend gate needs (GEO generate
// requires the Brand Strategy validated; every measurement requires both layers).
// ─────────────────────────────────────────────────────────────────────────────

const WIZARD_STEP_KEYS = ['onboarding.wizard.stepBrand', 'onboarding.wizard.stepMarket', 'onboarding.wizard.stepPrompts'];

export default function BrandOnboarding() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setActiveBrand, refreshBrands } = useBrand();

  if (!brandId) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ maxWidth: 480, width: '100%' }}>
          <Alert severity="error">{t('onboarding.noBrandSpecified')}</Alert>
        </Box>
      </Box>
    );
  }

  if (brandId === 'new') {
    return (
      <Shell step={0} title={t('onboarding.create.addBrandTitle')}>
        <BrandSetupStep
          onCreated={async (id) => {
            await refreshBrands();
            navigate(`/onboarding/brand/${id}`, { replace: true });
          }}
          onSkipped={async (brand: Brand, queue: PendingTarget[]) => {
            // The queued Cibles are written before leaving (nothing else would run them).
            if (queue.length) await runPendingTargets(brand.brandId, queue);
            await api.post(`/brands/${brand.brandId}/complete-onboarding`, {}).catch(() => void 0);
            await refreshBrands();
            setActiveBrand(brand);
            navigate('/strategy');
          }}
        />
      </Shell>
    );
  }

  return <BrandWizard key={brandId} brandId={brandId} />;
}

function BrandWizard({ brandId }: { brandId: string }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { brands, setActiveBrand, refreshBrands } = useBrand();
  const brand = brands.find((b) => b.brandId === brandId) || null;
  const [searchParams, setSearchParams] = useSearchParams();

  // ?gsc=<result>: back from the Search Console consent started on « Vos prompts » (the band's
  // `returnPathHere` is this URL). Handed to the band BEFORE page 3 mounts (it reads it on
  // mount), then stripped; page 3 opens « + Ajouter des prompts » on Search Console.
  const [gscReturn] = useState<GscCallbackResult | null>(() => {
    const r = searchParams.get('gsc');
    if (!isGscCallbackResult(r)) return null;
    setPendingGscReturn(r);
    return r;
  });
  useEffect(() => {
    if (searchParams.get('gsc') === null) return;
    setSearchParams((p) => { const n = new URLSearchParams(p); n.delete('gsc'); return n; }, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 1 = Votre marché, 2 = Vos prompts. A resumed brand whose GEO Strategy is already validated
  // (page 2 was saved) resumes on the prompts; decided once the brand list has the brand (a
  // reload, e.g. the Google redirect, starts with it still loading).
  const [step, setStep] = useState<1 | 2 | null>(() => (brand ? (gscReturn || brand.geoStrategyMeta?.status === 'validated' ? 2 : 1) : null));
  useEffect(() => {
    if (step === null && brand) setStep(gscReturn || brand.geoStrategyMeta?.status === 'validated' ? 2 : 1);
  }, [brand, step, gscReturn]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // « Quitter » (delete the brand) only when this ISN'T the tenant's only path into the app —
  // deleting its only onboarded brand would bounce OnboardingGate straight back here.
  const canManage = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'member';
  const canQuit = canManage && brands.some((b) => b.brandId !== brandId && isOnboardingComplete(b));

  const handleQuit = async () => {
    try {
      await api.delete(`/brands/${brandId}`);
    } catch (err) {
      // A partial delete hides the brand and keeps it retryable from the brands
      // settings tab (« Suppression incomplète ») — continue there.
      if (!(err instanceof ApiError && err.body?.code === 'BRAND_DELETE_PARTIAL')) throw err;
    }
    await refreshBrands();
    navigate('/settings?tab=brands');
  };

  const leaveTo = async (path: string, state?: Record<string, unknown>) => {
    await refreshBrands();
    if (brand) setActiveBrand(brand);
    navigate(path, state ? { state } : undefined);
  };

  const title = step !== 2
    ? t('onboarding.market.title', { name: brand?.name || t('onboarding.wizard.yourBrand') })
    : t('onboarding.promptsStep.title');

  return (
    <Shell step={step ?? 1} title={title} brandId={brandId} wide={step === 2}>
      {step === 1 && (
        <MarketStep
          brandId={brandId}
          brand={brand}
          onDone={async () => {
            await refreshBrands();
            setStep(2);
          }}
          onSaveQuit={async () => {
            // Owner 2026-11-03: the brand may stay without prompts, added later in Prompt Studio.
            await api.post(`/brands/${brandId}/complete-onboarding`, {}).catch(() => void 0);
            await leaveTo('/strategy');
          }}
          canQuit={canQuit}
          onQuit={handleQuit}
        />
      )}
      {step === 2 && brand && (
        <PromptsStep
          brandId={brandId}
          brand={brand}
          // `justOnboarded` offers the one-time dashboard product tour.
          onFinish={() => leaveTo('/', { justOnboarded: true })}
          onBack={() => setStep(1)}
          gscReturn={gscReturn}
          canQuit={canQuit}
          onQuit={handleQuit}
        />
      )}
    </Shell>
  );
}

function Shell({
  step,
  title,
  brandId,
  wide,
  children,
}: {
  step: number;
  title: string;
  brandId?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ minHeight: '100vh', py: 5, backgroundImage: ONBOARDING_BG }}>
      <Container maxWidth={wide ? 'lg' : 'md'}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          {brandId ? (
            <BrandFavicon brandId={brandId} highRes size={48} sx={{ mb: 1.5 }} />
          ) : (
            <Box component="img" src="/traaker-logo-compact.png" alt="Traaker" sx={{ height: 32, mb: 2.5 }} />
          )}
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em', textAlign: 'center' }}>
            {title}
          </Typography>
        </Box>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3.5 }}>
          {WIZARD_STEP_KEYS.map((labelKey) => (
            <Step key={labelKey}>
              <StepLabel>{t(labelKey)}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ maxWidth: wide ? 1000 : 860, mx: 'auto' }}>{children}</Box>
      </Container>
    </Box>
  );
}
